import React, { useEffect, useState, useRef } from "react";
import "./Navbar.css";
import { colors } from "./constants/colors";
import { hexToRGBA } from "./utils/colorUtils";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, X } from "lucide-react";

const useScrollHide = () => {
  const [isHidden, setIsHidden] = useState(false);
  const lastScrollTop = useRef(0);
  const ticking = useRef(false);
  const animationRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!ticking.current) {
        animationRef.current = window.requestAnimationFrame(() => {
          const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop;
          const scrollDelta = scrollTop - lastScrollTop.current;

          // Always show the navbar, changed to never hide
          setIsHidden(false);

          lastScrollTop.current = scrollTop;
          ticking.current = false;
        });

        ticking.current = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return isHidden;
};

// Combined links including contact
const links = [
  {
    ref: "about",
    text: "About",
    color: colors.primary.teal,
    isModal: false,
  },
  {
    ref: "photography",
    text: "Photography",
    color: colors.primary.pink,
    isModal: false,
  },
  {
    ref: "media",
    text: "Media",
    color: colors.primary.blue,
    isModal: false,
  },
];

const Navbar = ({ openContactModal, isVisible }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHidden = useScrollHide();
  const [transitionComplete, setTransitionComplete] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeLinkIndex, setActiveLinkIndex] = useState(null);
  const navRef = useRef(null);
  const logoRef = useRef(null);

  // Keep the spinning logo state
  const [spinLogo, setSpinLogo] = useState(true);
  // Add logo hover state for enhanced interactions
  const [isLogoHovered, setIsLogoHovered] = useState(false);

  // Force logo animation to work on component mount and whenever the spinLogo state changes
  useEffect(() => {
    if (logoRef.current) {
      if (spinLogo) {
        // Remove and re-add the class to restart the animation
        logoRef.current.classList.remove("spin-continuous");
        // Use a small timeout to ensure the DOM updates
        setTimeout(() => {
          if (logoRef.current) {
            logoRef.current.classList.add("spin-continuous");
          }
        }, 10);
      } else {
        logoRef.current.classList.remove("spin-continuous");
      }
    }
  }, [spinLogo]);

  // Handle clicks outside the menu to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        navRef.current &&
        !navRef.current.contains(event.target) &&
        isExpanded
      ) {
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isExpanded]);

  // Track window resize with debounce for better performance
  useEffect(() => {
    let resizeTimer;

    const handleResize = () => {
      clearTimeout(resizeTimer);

      resizeTimer = setTimeout(() => {
        setWindowWidth(window.innerWidth);
      }, 100);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  // Function to close the expanded menu
  const closeMenu = () => {
    setIsExpanded(false);
  };

  // Toggle the expanded state for menu
  const toggleExpanded = () => {
    // If already expanded, navigate to home instead of toggling
    if (isExpanded) {
      navigate("/");
      return;
    }

    setIsExpanded(true);

    // Always ensure spinning is on when opening the menu, but don't reset if already spinning
    if (!spinLogo) {
      setSpinLogo(true);
    }
  };

  // Handle link clicks - navigate or open modal with correct parameters
  const handleLinkClick = (e, link) => {
    e.preventDefault();

    if (link.isModal) {
      // Open the contact modal
      openContactModal();
    } else {
      // Navigate to the route
      navigate(`/${link.ref}`);
    }

    // Close the menu on any link click
    closeMenu();
  };

  useEffect(() => {
    // Get current path and set active link index
    const currentPath = location.pathname.substring(1); // Remove leading '/'
    const index = links.findIndex((link) => link.ref === currentPath);
    setActiveLinkIndex(index);

    const root = document.documentElement;
    const opacity = 0.4;

    switch (location.pathname) {
      case "/about":
        root.style.setProperty(
          "--navbar-bg",
          hexToRGBA(colors.pastel.teal, opacity)
        );
        break;
      case "/photography":
        root.style.setProperty(
          "--navbar-bg",
          hexToRGBA(colors.pastel.pink, opacity)
        );
        break;
      case "/media":
        root.style.setProperty(
          "--navbar-bg",
          hexToRGBA(colors.pastel.lightBlue, opacity)
        );
        break;
      default:
        root.style.setProperty(
          "--navbar-bg",
          hexToRGBA(colors.pastel.pink, opacity)
        );
        break;
    }
  }, [location.pathname]);

  return (
    <div
      className={`nav-container ${!isVisible ? "navbar-hidden-initial" : ""}`}
    >
      {/* Overlay for handling clicks outside */}
      {isExpanded && (
        <div className="menu-overlay active" onClick={closeMenu}></div>
      )}

      <nav
        ref={navRef}
        role="navigation"
        aria-label="Main Navigation"
        className={`navigation ${isHidden ? "hidden" : ""} ${
          transitionComplete ? "transition-complete" : ""
        } ${isExpanded ? "expanded" : ""}`}
      >
        {/* Back chevron for closing the menu */}
        {isExpanded && (
          <div
            className="back-chevron"
            onClick={(e) => {
              e.stopPropagation();
              closeMenu();
            }}
            style={{ pointerEvents: "auto" }}
            data-interactive="true"
            data-cursor-type="close"
          >
            <X size={20} />
          </div>
        )}

        <div className="nav-logo">
          <a
            href="#"
            data-interactive
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (isExpanded) {
                navigate("/");
                closeMenu();
                setSpinLogo(true);
                setIsLogoHovered(false);
              } else {
                toggleExpanded();
              }
            }}
            onMouseEnter={() => setIsLogoHovered(true)}
            onMouseLeave={() => setIsLogoHovered(false)}
          >
            <img
              ref={logoRef}
              src={process.env.PUBLIC_URL + "/EVEY LOGO.png"}
              alt="EVEY Logo"
              className={`hardware-accelerated ${
                spinLogo ? "spin-continuous" : ""
              }`}
              onDoubleClick={(e) => {
                e.stopPropagation();
                // Toggle the spinning state
                setSpinLogo(!spinLogo);
              }}
              data-interactive
            />
          </a>
        </div>

        <div className={`menu-links ${isExpanded ? "expanded" : ""}`}>
          {links.map((link, index) => (
            <a
              href={`/${link.ref}`}
              key={link.ref}
              className={`menu-link ${
                activeLinkIndex === index ? "active" : ""
              }`}
              data-index={index}
              style={{
                "--link-color": link.color,
                "--animation-delay": `${index * 0.1}s`,
              }}
              onClick={(e) => handleLinkClick(e, link)}
              data-interactive
            >
              <span className="link-text">{link.text}</span>
            </a>
          ))}

          <button
            className="nav-contact-button"
            onClick={() => {
              openContactModal();
              closeMenu();
            }}
            style={{ "--animation-delay": `${links.length * 0.1}s` }}
            data-interactive
          >
            <MessageCircle size={20} />
            <span>Let's Link!</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default Navbar;
